import React, {Dispatch, SetStateAction} from "react"
import {
    Column,
    DefaultFilterFunction,
    Filter,
    FilterFunction,
    FilterRender,
    ReactTableFunction,
    SortFunction,
} from "react-table"
import {
    Checkbox,
    CheckboxProps,
    Dropdown, Header,
    Icon,
    Label, Popup,
} from "semantic-ui-react"
import {
    IRawSupplierReference,
    ITranslations,
    IReferenceIds,
    IRawSupplierDeclinedReference,
} from "../../models"
import {Multilang} from "../../models/product/multilang-string"
import {IProductRefVente} from "../../models/product/productRefVente"
import {getlangFromCache, isDroopyGuid, Languages} from "../../utils"
import {getBruneauRefButton} from "../../utils/bruneau-button"
import {LocalStorageColumnsKeys} from "../../utils/local-storage-keys"
import {normalizeString} from "../../utils/string"
import {Copiable} from "./com_copiable"
import LazyImage from "./LazyImage"
import {$getValidationHistory} from "../../core/requests"
import Loading from "./com_loading";
import moment from "moment";
import {ensureCorrectPopupPosition} from "../../utils/popup-tools"

const ValidationPopup = ({
                             supplierName,
                             supplierRef,
                             trigger,
                         }: {
    supplierName: string
    supplierRef: string
    trigger: JSX.Element
}) => {
    const [history, setHistory] = React.useState<
        { username: string; date: string }[]
    >([])
    const [loading, setLoading] = React.useState(false)

    const handleOpen = () => {
        setLoading(true)
        $getValidationHistory(supplierName, supplierRef)
            .then((res) => setHistory(res))
            .finally(() => setLoading(false))
    }

    const content = loading ? (
        <Header as={"h4"}>Chargement...</Header>
    ) : history.length > 0 ? (
            <>
                <Header as={"h3"}>Historique de validation</Header>
                {
                    history.map((h, i) => (
                        <p>{h.username + " le " + moment(h.date, "DD/MM/YYYY HH:mm:ss").format("DD/MM/YYYY [à] HH:mm")}</p>))
                }
            </>
        ) :
        (
            <Header as={"h4"}>Jamais validé</Header>
        )

    return (
        <Popup
            trigger={trigger}
            onOpen={handleOpen}
            hoverable={true}
            on="hover"
            {...ensureCorrectPopupPosition}
            content={
                <div style={{maxHeight: "200px", overflowY: "auto", paddingRight: "1em"}}>
                    {content}
                </div>
            }
        />
    )
}

interface IGetColumnsProps {
    selectedRefs: IReferenceIds[]
    handleChangeSelectedRefs: (
        e: React.FormEvent<HTMLInputElement>,
        data: CheckboxProps
    ) => void
    isSubComponent?: boolean
    selectedValidatedFilter: "All" | "True" | "False"
    setSelectedValidatedFilter: Dispatch<SetStateAction<"All" | "True" | "False">>
    selectedActivatedFilter: "All" | "1" | "0"
    setSelectedActivatedFilter: Dispatch<SetStateAction<"All" | "1" | "0">>
    isgUrl: string
    activeLang: Languages
    refVentes: IProductRefVente[]
    rawComTree: any[]
    translations: { [x: string]: string }
    showImages: boolean
    showDeletedRefs: boolean
    idSocieteg: string
    supplierName: string
}

export const getColumns = ({
                               selectedRefs,
                               refVentes,
                               isgUrl,
                               handleChangeSelectedRefs,
                               isSubComponent = false,
                               activeLang,
                               rawComTree,
                               translations,
                               showImages,
                               showDeletedRefs,
                               idSocieteg,
                               supplierName,
                               selectedValidatedFilter,
                               setSelectedValidatedFilter,
                               selectedActivatedFilter,
                               setSelectedActivatedFilter,
                           }: IGetColumnsProps) => {
    const columns: Array<Column<IRawSupplierReference>> = [
        {
            id: "selectButton",
            Header: "",
            width: 30,
            accessor: (a: IRawSupplierReference) => {
                const checked = selectedRefs.some(
                    (selected: IReferenceIds) => selected.droopyId === a.droopyId
                )
                return (
                    <Checkbox
                        placeholder={"checkbox " + a.droopyId}
                        onChange={handleChangeSelectedRefs}
                        checked={checked}
                        value={JSON.stringify(a)}
                        color="violet"
                    />
                )
            },
            filterable: false,
        },
        {
            Header: translations.idColumnLabel,
            className: "clickable",
            id: LocalStorageColumnsKeys.SUPPLIER_REF,
            width: 190,
            accessor: (a: IRawSupplierReference) => {
                const ref = isDroopyGuid(a.supplierRef) ? (
                    <b>Ref. à renseigner</b>
                ) : (
                    a.supplierRef
                )
                return a.isTopRef ? <></> : ref
            },
        },
        {
            Header: translations.labelColumnLabel,
            className: "clickable",
            id: LocalStorageColumnsKeys.LABEL,
            width: 400,
            accessor: (a: IRawSupplierReference) => (
                <div className="text-div">
                    {Multilang.String.GetValue(a.label, activeLang)}
                    {!a.validatedOnce && (
                        <Label className="new-ribbon" color="red" ribbon="right">
                            New
                        </Label>
                    )}
                </div>
            ),
            sortMethod: textSortMethod,
            filterMethod: textFilterMethod(activeLang),
        },
        {
            Header: translations.comNodeColumnLabel,
            className: "clickable",
            id: LocalStorageColumnsKeys.COM_TREE,
            width: 190,
            accessor: (a: IRawSupplierReference) => {
                const nodeCode = a.comNode
                const n = rawComTree.find((node) => node.code === nodeCode)
                return <div className="text-div">{n ? n.label : ""}</div>
            },
            sortMethod: textSortMethod,
            filterMethod: nodeFilterMethod(rawComTree),
        },
        {
            Header: translations.imageColumnLabel,
            className: "clickable",
            id: "pictures",
            width: 55,
            accessor: (a: IRawSupplierReference) => {
                const displayImageUrl = a.picture?.path
                const displayImageName = a.picture?.name
                return showImages ? (
                    displayImageUrl && displayImageName && (
                        <LazyImage
                            src={window.location.origin + "/api/image?url=" + displayImageUrl}
                            alt={displayImageName}
                            // loading="lazy"
                            style={{height: "40px", width: "auto"}}
                        />
                    )
                ) : (
                    <div className="text-div-align">
                        {translations.imageDisplayDisabled}
                    </div>
                )
            },
            sortable: false,
            filterable: false,
        },
        {
            Header: translations.priceColumnLabel,
            className: "clickable",
            accessor: LocalStorageColumnsKeys.PRICE,
        },
        {
            Header: translations.eanColumnLabel,
            className: "clickable",
            accessor: LocalStorageColumnsKeys.EAN,
            width: 111,
        },
        {
            Header: translations.manufacturerRefColumnLabel,
            className: "clickable",
            accessor: LocalStorageColumnsKeys.MANUFACTURER_REF,
            width: 190,
        },
        {
            Header: translations.dateCreatLabel,
            className: "clickable",
            accessor: LocalStorageColumnsKeys.DATE_CREAT,
            sortMethod: dateSortMethod,
            width: 81,
        },
        {
            Header: translations.dateModifLabel,
            className: "clickable",
            accessor: LocalStorageColumnsKeys.DATE_MAJ,
            sortMethod: dateSortMethod,
            width: 81,
        },
        {
            Header: translations.activatedColumnLabel,
            className: "clickable",
            id: "activated",
            accessor: (a: IRawSupplierReference) => {
                return a.isTopRef
                    ? ""
                    : a.activated
                        ? translations.yes
                        : translations.no
            },
            Filter: activatedFilter(
                translations,
                selectedActivatedFilter,
                setSelectedActivatedFilter
            ),
            filterMethod: activatedFilterMethod,
        },
        {
            Header: "Ref. Bruneau",
            className: "clickable",
            id: LocalStorageColumnsKeys.REF_VENTE,
            sortable: true,
            sortMethod: textSortMethod,
            accessor: (a: IRawSupplierReference) => {
                const r = refVentes.find((o) => o.refData === a.supplierRef)
                return (
                    <Copiable
                        text={r?.refVente}
                        component={<>{r?.refVente}</>}
                        hoverText={"Copy"}
                        clickedText={translations.refVenteClickPopupText}
                    />
                )
            },
        },
        {
            Header: translations.validatedColumnLabel,
            className: "clickable",
            id: "validated",
            accessor: (a: IRawSupplierReference) => {
                const r = refVentes.find((o) => o.refData === a.supplierRef)

                const icon = <ValidationPopup
                    supplierName={supplierName}
                    supplierRef={a.supplierRef}
                    trigger={a.validated ? (
                        <Icon
                            className="check valid"
                            color="green"
                            size="large"
                            value={1}
                        />

                    ) : (
                        <Icon className="delete invalid" size="large" value={0}/>
                    )}
                />

                return showDeletedRefs || a.isTopRef ? (
                    <></>
                ) : (
                    <>
                        {icon}
                        <Copiable
                            text={r?.refVente}
                            component={getBruneauRefButton(isgUrl, r?.refVente, r?.isRefStock)}
                            hoverText={
                                translations.refVenteHoverPopupText + " " + r?.refVente
                            }
                            clickedText={translations.refVenteClickPopupText}
                        />
                    </>
                )
            },
            sortMethod: validatedSortMethod,
            Filter: validatedFilter(
                translations,
                selectedValidatedFilter,
                setSelectedValidatedFilter
            ),
            filterMethod: validatedFilterMethod,
        },
    ]

    let firstCol
    if (!isSubComponent) {
        firstCol = {
            expander: true,
            width: 30,
            Expander: ({
                           isExpanded,
                           original,
                       }: {
                isExpanded: true
                original: IRawSupplierReference
            }) => {
                return (
                    original.isTopRef && <Icon name={isExpanded ? "minus" : "plus"}/>
                )
            },
        }
    } else {
        firstCol = {
            accessor: () => (
                <Icon name="share" className="cloud vertically flipped icon"/>
            ),
            width: 30,
        }
    }

    return [firstCol, ...columns]
}

const activatedFilter =
    (
        translations: ITranslations["productList"],
        selectedActivatedFilter: "All" | "1" | "0",
        setSelectedActivatedFilter: Dispatch<SetStateAction<"All" | "1" | "0">>
    ): FilterRender =>
        (params: {
            column: Column
            filter: any
            onChange: ReactTableFunction
            key?: string
        }) => {
            const options = [
                {key: "All", value: "All", text: translations.all},
                {key: "1", value: true, text: translations.yes},
                {key: "0", value: false, text: translations.no},
            ]
            const handleChange = (e: any, {value}: any) => {
                setSelectedActivatedFilter(value)
                params.onChange(value)
            }
            return (
                <Dropdown
                    className="acti"
                    value={selectedActivatedFilter}
                    selection
                    fluid
                    onChange={handleChange}
                    options={options}
                />
            )
        }

const activatedFilterMethod: DefaultFilterFunction = (
    {id, value}: Filter,
    row: any,
    column: any
) => {
    let match =
        value === "All" ||
        (!!(row?._original?.activated === value) && !row?._original?.isTopRef)

    const orig = row._original
    if (orig && orig.isTopRef && orig.declined) {
        orig.declined.forEach((p: IRawSupplierDeclinedReference) => {
            match = match || !!(p.activated === value)
        })
    }

    return match
}

const validatedFilter =
    (
        translations: ITranslations["productList"],
        selectedValidatedFilter: "All" | "True" | "False",
        setSelectedValidatedFilter: Dispatch<
            SetStateAction<"All" | "True" | "False">
        >
    ): FilterRender =>
        (params: {
            column: Column
            filter: any
            onChange: ReactTableFunction
            key?: string
        }) => {
            const IconTrue = (
                <span>
        <Icon className="check valid" size="large"/>
                    {translations.validated}
      </span>
            )
            const IconFalse = (
                <span>
        <Icon className="delete invalid" size="large" style={{}}/>
                    {translations.notValidated}
      </span>
            )
            const IconAll = (
                <span>
        <Icon className="minus" color="grey" size="large"/>
                    {translations.all}
      </span>
            )
            const options = [
                {key: "All", value: "All", text: IconAll},
                {key: "True", value: true, text: IconTrue},
                {key: "False", value: false, text: IconFalse},
            ]
            const handleChange = (e: any, {value}: any) => {
                setSelectedValidatedFilter(value)
                params.onChange(value)
            }
            return (
                <Dropdown
                    value={selectedValidatedFilter}
                    selection
                    fluid
                    onChange={handleChange}
                    options={options}
                />
            )
        }

const validatedFilterMethod: DefaultFilterFunction = (
    {id, value}: Filter,
    row: any,
    column: any
) => {
    let match = !!(value === "All" || !!(row?._original?.validated === value))

    const orig = row._original
    if (orig && orig.isTopRef && orig.declined) {
        orig.declined.forEach((p: IRawSupplierDeclinedReference) => {
            match = match || !!(p.validated === value)
        })
    }

    return match
}

const nodeFilterMethod =
    (rawComTree: any[]): DefaultFilterFunction =>
        ({id, value}, row) => {
            let match = false
            let text = normalizeString(row[id]?.props?.children || "")
            const filter = normalizeString(value)
            match = text.includes(filter)

            // const orig = row._original
            // if (orig && orig.isTopRef && orig.declined) {
            //   orig.declined.forEach((p: IRawSupplierDeclinedReference) => {
            //     const n = rawComTree.find((node) => node.code === p.comNode)
            //     text = n?.label || ""
            //     match = match || text.includes(filter)
            //   })
            // }

            return match
        }

const textFilterMethod =
    (activeLang: Languages): DefaultFilterFunction =>
        ({id, value}, row) => {
            let match = false
            const rawText = Multilang.String.IsMultilangString(row[id]?.props?.children)
                ? Multilang.String.GetValue(
                    row[id].props.children,
                    getlangFromCache(),
                    false
                )
                : row[id]?.props?.children.toString()
            let text = normalizeString(rawText)
            const filter = normalizeString(value)
            match = text.includes(filter)

            const orig = row._original
            if (orig && orig.isTopRef && orig.declined) {
                orig.declined.forEach((p: IRawSupplierDeclinedReference) => {
                    const mstring = Multilang.String.GetValue(p[id], activeLang) || ""
                    text = normalizeString(mstring)
                    match = match || text.includes(filter)
                })
            }

            return match
        }

const textSortMethod: SortFunction = (a: JSX.Element, b: JSX.Element) => {
    const formattedA = normalizeString(a?.props?.children || a?.props?.text || "")
    const formattedB = normalizeString(b?.props?.children || b?.props?.text || "")
    if (formattedA > formattedB) {
        return 1
    }
    if (formattedA < formattedB) {
        return -1
    }
    return 0
}

const dateSortMethod: SortFunction = (a: string, b: string) => {
    const formattedA = a.split("/").reverse().join("/")
    const formattedB = b.split("/").reverse().join("/")
    if (formattedA > formattedB) {
        return 1
    }
    if (formattedA < formattedB) {
        return -1
    }
    return 0
}

const validatedSortMethod: SortFunction = (a: JSX.Element, b: JSX.Element) => {
    const formattedA = a.props.value
    const formattedB = b.props.value
    if (formattedA > formattedB) {
        return 1
    }
    if (formattedA < formattedB) {
        return -1
    }
    return 0
}
